import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import FeatureUtils from '@appblocks/codegeneration/FeatureUtils';
import {
    SettingType,
    BlockType,
    Define,
    ValidationMessage,
    TibboDataType,
    SettingOption,
    CodeBlock,
    TimeFormats,
    ValidationMessageType,
    SettingState,
} from '@appblocks/codegeneration/types';
import PlatformUtils, { TIBBO_DEVICE_ID } from '@appblocks/platforms/PlatformUtils';

export default class General extends BaseFeature {
    constructor() {
        super();
        this.name = 'General';
        this.id = 'general';
        this.priority = 99;
        this.icon = 'FaInfoCircle';

        this.files = [
            'libraries/time/time.tbh',
            'libraries/time/time.tbs',
            'libraries/utils/utils.tbh',
            'libraries/utils/utils.tbs',
        ];

        const settings = [
            {
                id: 'LEDPAT',
                name: 'Standard LED Patterns',
                settingType: SettingType.OPTION,
                default: '1',
                required: true,
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
                helpText: 'When enabled, LED patterns are used to indicate the device\'s status.',
            },
            {
                id: 'DEVICE_NAME',
                name: 'Device Name',
                settingType: SettingType.STRING,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'GNAME',
                    NAME: 'Device Name',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MAX: 32,
                    DEFAULT: 'Device_Name_1',
                },
                UIControl: {
                    CONTROL: 'EDIT',
                },
                required: true,
                default: 'Device_Name_1',
                helpText: 'Give your device a name or link it to a setting.',
            },
            {
                id: 'FIRMWAREVERSION',
                name: 'Application Version',
                settingType: SettingType.STRING,
                required: true,
                default: '1.0.0',
                helpText: 'Your application\'s version number, which is shown in the Device Explorer.',
            },
            {
                id: 'INTERFACE_SWITCHING',
                name: 'Automatic Interface Switching',
                settingType: SettingType.OPTION,
                default: '1',
                required: true,
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
                helpText: `
When enabled, all socket connections will go through the best available network interface. The priority is as follows:
1. Ethernet
2. Wi-Fi
3. Cellular
`,
            },
            {
                id: 'DEFAULT_INTERFACE',
                name: 'Default Interface',
                settingType: SettingType.OPTION,
                default: '1',
                required: true,
                options: [],
                helpText: `
The device will use this interface when it is ready.
`,
            },
            {
                id: 'TIMEZONE',
                name: 'Time Zone',
                settingType: SettingType.OPTION,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'TZONE',
                    NAME: 'Device Time Zone',
                    SETTINGTYPE: 'string',
                    MIN: 0,
                    MAX: 3,
                    DATATYPE: 'S',
                    DEFAULT: '0',
                },
                UIControl: {
                    CONTROL: 'STATIC',
                    OPTIONS: [
                        {
                            name: 'UTC-12:00',
                            value: '0',
                        },
                        {
                            name: 'UTC-11:00',
                            value: '1',
                        },
                        {
                            name: 'UTC-10:00',
                            value: '2',
                        },
                        {
                            name: 'UTC-09:00',
                            value: '3',
                        },
                        {
                            name: 'UTC-08:00',
                            value: '4',
                        },
                        {
                            name: 'UTC-07:00',
                            value: '5',
                        },
                        {
                            name: 'UTC-06:00',
                            value: '6',
                        },
                        {
                            name: 'UTC-05:00',
                            value: '7',
                        },
                        {
                            name: 'UTC-04:30',
                            value: '8',
                        },
                        {
                            name: 'UTC-04:00',
                            value: '9',
                        },
                        {
                            name: 'UTC-03:30',
                            value: '10',
                        },
                        {
                            name: 'UTC-03:00',
                            value: '11',
                        },
                        {
                            name: 'UTC-02:00',
                            value: '12',
                        },
                        {
                            name: 'UTC-01:00',
                            value: '13',
                        },
                        {
                            name: 'UTC',
                            value: '14',
                        },
                        {
                            name: 'UTC+01:00',
                            value: '15',
                        },
                        {
                            name: 'UTC+02:00',
                            value: '16',
                        },
                        {
                            name: 'UTC+03:00',
                            value: '17',
                        },
                        {
                            name: 'UTC+03:30',
                            value: '18',
                        },
                        {
                            name: 'UTC+04:00',
                            value: '19',
                        },
                        {
                            name: 'UTC+04:30',
                            value: '20',
                        },
                        {
                            name: 'UTC+05:00',
                            value: '21',
                        },
                        {
                            name: 'UTC+05:30',
                            value: '22',
                        },
                        {
                            name: 'UTC+05:45',
                            value: '23',
                        },
                        {
                            name: 'UTC+06:00',
                            value: '24',
                        },
                        {
                            name: 'UTC+06:30',
                            value: '25',
                        },
                        {
                            name: 'UTC+07:00',
                            value: '26',
                        },
                        {
                            name: 'UTC+08:00',
                            value: '27',
                        },
                        {
                            name: 'UTC+09:00',
                            value: '28',
                        },
                        {
                            name: 'UTC+9:30',
                            value: '29',
                        },
                        {
                            name: 'UTC+10:00',
                            value: '30',
                        },
                        {
                            name: 'UTC+11:00',
                            value: '31',
                        },
                        {
                            name: 'UTC+12:00',
                            value: '32',
                        },
                        {
                            name: 'UTC+13:00',
                            value: '33',
                        },
                    ],
                },
                required: true,
                default: '0',
                options: [
                    {
                        name: 'UTC-12:00',
                        value: '0',
                    },
                    {
                        name: 'UTC-11:00',
                        value: '1',
                    },
                    {
                        name: 'UTC-10:00',
                        value: '2',
                    },
                    {
                        name: 'UTC-09:00',
                        value: '3',
                    },
                    {
                        name: 'UTC-08:00',
                        value: '4',
                    },
                    {
                        name: 'UTC-07:00',
                        value: '5',
                    },
                    {
                        name: 'UTC-06:00',
                        value: '6',
                    },
                    {
                        name: 'UTC-05:00',
                        value: '7',
                    },
                    {
                        name: 'UTC-04:30',
                        value: '8',
                    },
                    {
                        name: 'UTC-04:00',
                        value: '9',
                    },
                    {
                        name: 'UTC-03:30',
                        value: '10',
                    },
                    {
                        name: 'UTC-03:00',
                        value: '11',
                    },
                    {
                        name: 'UTC-02:00',
                        value: '12',
                    },
                    {
                        name: 'UTC-01:00',
                        value: '13',
                    },
                    {
                        name: 'UTC',
                        value: '14',
                    },
                    {
                        name: 'UTC+01:00',
                        value: '15',
                    },
                    {
                        name: 'UTC+02:00',
                        value: '16',
                    },
                    {
                        name: 'UTC+03:00',
                        value: '17',
                    },
                    {
                        name: 'UTC+03:30',
                        value: '18',
                    },
                    {
                        name: 'UTC+04:00',
                        value: '19',
                    },
                    {
                        name: 'UTC+04:30',
                        value: '20',
                    },
                    {
                        name: 'UTC+05:00',
                        value: '21',
                    },
                    {
                        name: 'UTC+05:30',
                        value: '22',
                    },
                    {
                        name: 'UTC+05:45',
                        value: '23',
                    },
                    {
                        name: 'UTC+06:00',
                        value: '24',
                    },
                    {
                        name: 'UTC+06:30',
                        value: '25',
                    },
                    {
                        name: 'UTC+07:00',
                        value: '26',
                    },
                    {
                        name: 'UTC+08:00',
                        value: '27',
                    },
                    {
                        name: 'UTC+09:00',
                        value: '28',
                    },
                    {
                        name: 'UTC+9:30',
                        value: '29',
                    },
                    {
                        name: 'UTC+10:00',
                        value: '30',
                    },
                    {
                        name: 'UTC+11:00',
                        value: '31',
                    },
                    {
                        name: 'UTC+12:00',
                        value: '32',
                    },
                    {
                        name: 'UTC+13:00',
                        value: '33',
                    },
                ],
            },
            {
                id: 'LOGGING',
                name: 'Debug Print Output',
                settingType: SettingType.OPTION,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'LOGGING',
                    NAME: 'Device Logging Method',
                    SETTINGTYPE: 'string',
                    MIN: 0,
                    MAX: 15,
                    DATATYPE: 'S',
                    DEFAULT: 'sys.debugprint',
                },
                UIControl: {
                    CONTROL: 'STATIC',
                    OPTIONS: [
                        {
                            name: 'Console',
                            value: 'sys.debugprint',
                        },
                        {
                            name: 'Store in Logs',
                            value: 'tbl_log',
                        },
                        {
                            name: 'LCD',
                            value: 'lcd_log',
                        },
                    ],
                },
                required: true,
                default: '',
                options: [
                    {
                        name: 'Console',
                        value: 'sys.debugprint',
                    },
                ],
                helpText: 'Determines where log data are displayed.',
            },
            {
                id: 'BEEPFREQUENCY',
                name: 'Buzzer Frequency',
                settingType: SettingType.NUMBER,
                required: true,
                min: 2,
                max: 65535,
                default: '2700',
            },
            {
                id: 'LATITUDE',
                name: 'Latitude',
                settingType: SettingType.NUMBER,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'LAT',
                    NAME: 'Latitude',
                    SETTINGTYPE: 'float',
                    DATATYPE: 'B',
                    MIN: -90,
                    MAX: 90,
                    DEFAULT: '0',
                },
                UIControl: {
                    CONTROL: 'EDIT',
                },
                required: true,
                min: -90,
                max: 90,
                default: '0',
            },
            {
                id: 'LONGITUDE',
                name: 'Longitude',
                settingType: SettingType.NUMBER,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'LNG',
                    NAME: 'Longitude',
                    SETTINGTYPE: 'float',
                    DATATYPE: 'B',
                    MIN: -180,
                    MAX: 180,
                    DEFAULT: '0',
                },
                UIControl: {
                    CONTROL: 'EDIT',
                },
                required: true,
                min: -180,
                max: 180,
                default: '0',
            },
            {
                id: 'TIME_FORMAT',
                name: 'Time format',
                settingType: SettingType.OPTION,
                default: TimeFormats.twelveHour,
                required: true,
                options: [
                    {
                        name: '12-hour',
                        value: TimeFormats.twelveHour,
                    },
                    {
                        name: '24-hour',
                        value: TimeFormats.twentyfourHour,
                    },
                ],
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    validate(): Array<ValidationMessage> {
        const errors: Array<ValidationMessage> = [];
        if (this.project.device.id === 'WM2000') {
            if (this.getSettingValue('INTERFACE_SWITCHING') === '1') {
                errors.push({
                    messageType: ValidationMessageType.UPDATE,
                    message: '',
                    featureId: this.getID(),
                    settingId: 'INTERFACE_SWITCHING',
                    data: '0',
                });
            }
        }
        return errors;
    }

    getState(settingId: string): SettingState {
        const capabilities = PlatformUtils.getCapabilities(this.project);
        const INTERFACE_SWITCHING = this.getSettingValue('INTERFACE_SWITCHING');
        if (settingId === 'DEFAULT_INTERFACE') {
            if (INTERFACE_SWITCHING === '1') {
                return SettingState.HIDDEN;
            }
        }
        if (settingId === 'LOGGING') {
            if (capabilities.includes('tios_g1')) {
                return SettingState.HIDDEN;
            }
        }
        return super.getState(settingId);
    }

    getDependencies() {
        const dependencies = [];
        if (this.project.device.id !== 'EM510W') {
            dependencies.push('datetime');
        }
        const LOGGING = this.getSettingValue('LOGGING', true);
        if (LOGGING === '1') {
            dependencies.push('tbl');
        }
        for (let i = 0; i < this.project.features.length; i++) {
            const feature = this.project.features[i];
            if (feature.id === 'linked_setting') {
                if (feature.getSettingValue('FEATURE') === this.getSettingValue('ID')
                    && (
                        feature.getSettingValue('SETTINGID') === 'TIMEZONE'
                        || feature.getSettingValue('SETTINGID') === 'DEVICE_NAME'
                        || feature.getSettingValue('SETTINGID') === 'LOGGING'
                        || feature.getSettingValue('SETTINGID') === 'LATITUDE'
                        || feature.getSettingValue('SETTINGID') === 'LONGITUDE'
                    )) {
                    dependencies.push('stg');
                }
            }
        }
        return dependencies;
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        const { project } = this;
        if (settingId === 'LOGGING') {
            const options = [
                {
                    name: 'Console',
                    value: 'sys.debugprint',
                },
                {
                    name: 'Store in Logs',
                    value: 'tbl_log',
                },
            ];
            if (this.project.device.platform === 'TPP2W(G2)') {
                const TPB = project.getSettingValue('boardsetting', 'TPB');
                if (TPB === 'tpb2lg2') {
                    options.push({
                        name: 'LCD Logging',
                        value: 'lcd_log',
                    });
                }
            }
            return options;
        }
        if (settingId === 'DEFAULT_INTERFACE') {
            // see if has cellular
            const cellular = this.project.getFeature('cell');
            const net = this.project.getFeature('net');
            const wifi = this.project.getFeature('wifi');
            const options = [];
            if (net && net.getEnabled()) {
                options.push({
                    name: 'Ethernet',
                    value: 'PL_SOCK_INTERFACE_NET',
                });
            }
            if (wifi && wifi.getEnabled()) {
                options.push({
                    name: 'Wi-Fi',
                    value: 'PL_SOCK_INTERFACE_WLN',
                });
            }
            if (cellular && cellular.getEnabled()) {
                options.push({
                    name: 'Cellular',
                    value: 'PL_SOCK_INTERFACE_PPP',
                });
            }
            return options;
        }
        return super.getSettingOptions(settingId);
    }

    getDefines(): Define[] {
        const defines = [
            {
                name: 'SOCK_MAX_SIGNATURE_LEN',
                value: '10',
            },
            {
                name: 'SSI_MAX_SIGNATURE_LEN',
                value: '32',
            },
            {
                name: 'SI2C_MAX_SIGNATURE_LEN',
                value: '32',
            },
            {
                name: 'SSPI_MAX_SIGNATURE_LEN',
                value: '32',
            },
        ];

        // if G2 or G3
        const TLS_PLATFORMS = ['TPP2W(G2)', 'TPP2W(G3)', 'TPP3W(G2)', 'TPP3W(G3)'];
        if (TLS_PLATFORMS.includes(this.project.device.id)) {
            defines.push({
                name: 'SUPPORTS_TLS',
                value: '1',
            });
        }

        return defines;
    }

    getCodeBlocks(): CodeBlock[] {
        const blocks: CodeBlock[] = [];

        const LOGGING = this.getSettingValue('LOGGING');
        const LATITUDE = this.getSettingValue('LATITUDE');
        const LONGITUDE = this.getSettingValue('LONGITUDE');
        const TIMEZONE = this.getSettingValue('TIMEZONE');
        if (this.project.runtime === 'simulator') {
            blocks.push({
                location: 'SIMULATOR_OS',
                blockType: BlockType.CODE,
                template: `
// objects
const PL_PAT_NOINT = 0;
const PL_PAT_CANINT = 1;
const pat = {
   play: (pattern) => {
        simulator.emit({
            eventType: 'pat.play',
            pattern: pattern,
        });
        console.log('playing led pattern: ' + pattern);
   }
};
const beep = {
    play: (pattern, canInt) => {
        simulator.emit({
            eventType: 'beep.play',
            pattern: pattern,
            canInt: canInt,
        });
        console.log('playing buzzer pattern: ' + pattern);
    },
};
const romfiles = {
    open: () => {},
    close: () => {},
};
`,
            });

            blocks.push({
                location: 'SIMULATOR_OS',
                blockType: BlockType.CODE,
                template: `
const MD_PRESSED = 1;
const MD_RELEASED = 0;
`,
            });

            blocks.push({
                location: 'on_md',
                blockType: BlockType.CODE,
                template: `
    if (value === MD_PRESSED) {
        /****on_button_pressed****/
    }
    if (value === MD_RELEASED) {
        /****on_button_released****/
    }`,
            });

            blocks.push({
                location: 'FUNCTION_DEFINITIONS',
                blockType: BlockType.CODE,
                template: `
function on_md(value) {
    /****on_md****/
}
`,
            });

            blocks.push({
                location: 'GLOBAL_DECLARATIONS',
                blockType: BlockType.CODE,
                template: `
const APP_LOGGING=${LOGGING}`,
            });

            if (LOGGING === '"tbl_log"') {
                blocks.push({
                    location: 'CONSOLE_LOG',
                    blockType: BlockType.CODE,
                    template: `
    if (simulator.tables['DEBUG']) {
        tbl_web_add_row('DEBUG', [getDatetimeUTC().toString(), [...args].join(',')]);
    }
                `,
                });
            }


            blocks.push({
                location: 'GLOBAL_DECLARATIONS',
                blockType: BlockType.CODE,
                template: `
const APP_TIMEZONE=${TIMEZONE}
const APP_LATITUDE=${LATITUDE}
const APP_LONGITUDE=${LONGITUDE}
            `,
            });

            if (this.project.runtime === 'simulator' && this.project.device.id.startsWith('TPP2')) {
                blocks.push({
                    location: 'SIMULATOR_OS',
                    blockType: BlockType.CODE,
                    template: `
    const lcd = {
        // properties and functions
        forecolor: () => {},
        backcolor: () => {},
        fill: () => {},
        print: () => {},
        lock: () => {},
        unlock: () => {},
        setfont: () => {},
        textorientation: 0,
        texthorizontalspacing: 0,
        textverticalspacing: 0,
        width: 320,
        height: 240,
        printaligned: () => {},
    };
    `,
                });

                blocks.push({
                    location: 'FUNCTION_DEFINITIONS',
                    blockType: BlockType.CODE,
                    template: `
function button_triggered(state, button) {
    /****button_triggered****/
}`,
                });
            }

            return blocks;
        }

        const INTERFACE_SWITCHING = this.getSettingValue('INTERFACE_SWITCHING') === '1';
        const DEFAULT_INTERFACE = this.getSettingValue('DEFAULT_INTERFACE');
        const { project } = this;
        const WIFI_ENABLED = project.getFeature('wifi')?.getSettingValue('ENABLED');
        const CELLULAR_ENABLED = project.getFeature('cell')?.getSettingValue('ENABLED');
        const APPLICATION_VERSION = this.getSettingValue('FIRMWAREVERSION');
        const APPLICATION_ID = project.id;
        const DEVICE_NAME = project.name;
        const capabilities = PlatformUtils.getCapabilities(project);
        let debugPrintOverride = false;
        for (let i = 0; i < this.project.features.length; i++) {
            const feature = this.project.features[i];
            if (feature.id === 'linked_setting') {
                if (feature.getSettingValue('FEATURE') === this.getSettingValue('ID')
                    && feature.getSettingValue('SETTINGID') === 'LOGGING') {
                    debugPrintOverride = true;
                }
            }
        }
        if (this.getSettingValue('LOGGING', true) !== 'sys.debugprint') {
            debugPrintOverride = true;
        }

        blocks.push({
            location: 'GLOBAL_CONSTS',
            blockType: BlockType.CODE,
            template: `
const FIRMWARE_VERSION="{${project.name} ${APPLICATION_VERSION}}"
`,
        });

        blocks.push({
            location: 'GLOBAL_DEFINES',
            blockType: BlockType.CODE,
            template: `
#define INTERFACE_SWITCHING ${INTERFACE_SWITCHING ? '1' : '0'}
#define DEFAULT_INTERFACE ${!INTERFACE_SWITCHING ? DEFAULT_INTERFACE : 'PL_SOCK_INTERFACE_NULL'}
`,
        });


        blocks.push({
            location: 'DEVICE_TBS',
            blockType: BlockType.CODE,
            template: `
sub interface_set(interface as pl_sock_interfaces, state as no_yes)
    dim best_interface as pl_sock_interfaces=DEFAULT_INTERFACE
    dim cell_starting as boolean=false

    if interface_ready(interface) <> state then
        dim print_string as string(50) = ""
        if interface=PL_SOCK_INTERFACE_NET then
            print_string = print_string + "NET>"
        else if interface=PL_SOCK_INTERFACE_WLN then
            print_string = print_string + "WiFi>"
        else if interface=PL_SOCK_INTERFACE_PPP then
            print_string = print_string + "CELL>"
        end if
        if state=YES then
            print_string = print_string + " enabled"
            if interface=PL_SOCK_INTERFACE_NET then
                print_string = print_string + ", IP: " + net.ip
            else if interface=PL_SOCK_INTERFACE_WLN then
            #IF WLN_AVAILABLE
                print_string = print_string + ", IP: " + wln.ip
            #ENDIF
            else if interface=PL_SOCK_INTERFACE_PPP then
                print_string = print_string + ", IP: " + ppp.ip
            end if
        else
            print_string = print_string + " disabled"
        end if

        sys.debugprint(print_string + "\\r\\n")
    end if
    
    if state = YES then
        interface_ready(interface) = YES
    else
        interface_ready(interface) = NO
    end if
    #IF INTERFACE_SWITCHING
    if interface_ready(PL_SOCK_INTERFACE_NET) = YES then
        best_interface = PL_SOCK_INTERFACE_NET
    else
        #IF WLN_AVAILABLE
            if interface_ready(PL_SOCK_INTERFACE_WLN) = YES then
                best_interface = PL_SOCK_INTERFACE_WLN
            end if
        #ENDIF
        if best_interface=PL_SOCK_INTERFACE_NULL then
        #IF CELLULAR_AVAILABLE
            if interface_ready(PL_SOCK_INTERFACE_PPP) = YES then
                best_interface = PL_SOCK_INTERFACE_PPP
                cell_started=false
            else
                cell_start()
                cell_starting=true
                cell_started=true
            end if
        #ENDIF
        end if
    end if
    #IF CELLULAR_AVAILABLE
    if best_interface<>PL_SOCK_INTERFACE_PPP AND cell_starting=false AND cell_sms_enabled=false then
        cell_started=false
        signal_strength_set(0)
        cell_stop()
        cell_started=false
    end if
    #ENDIF
    #ENDIF

    if interface_ready(best_interface)=NO then
        best_interface=PL_SOCK_INTERFACE_NULL
    end if
    change_current_interface(best_interface)
    '****interface_set****'
end sub
`,
        });

        blocks.push({
            location: 'GLOBAL_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `
declare sub interface_set(interface as pl_sock_interfaces, state as no_yes)
declare sub callback_system_error(byref tag as string, byref message as string)`,
        });

        blocks.push({
            location: 'GLOBAL_CONSTS',
            blockType: BlockType.CODE,
            template: 'const MAX_NUM_INTERFACES=4',
        });

        blocks.push({
            location: 'GLOBAL_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `
declare interface_ready(MAX_NUM_INTERFACES) as no_yes`,
        });

        blocks.push({
            location: 'DEVICE_TBS',
            blockType: BlockType.CODE,
            template:
                `
sub close_interface_sockets(interface as pl_sock_interfaces)
    dim f,sock_num_bup as byte
    dim i as word

    sock_num_bup=sock.num

    for f=0 to sock.numofsock-1
        sock.num=f
        if sock.targetinterface=interface then
            sock.close()
            sock.discard()
        end if
    next f

    i=sys.timercount

    wait_close_interface_sockets:
    for f=0 to sock.numofsock-1
        sock.num=f
        if sock.targetinterface=interface and sock.statesimple<>PL_SSTS_CLOSED and sys.timercount-i<5 and sys.timercount>=i then
            goto wait_close_interface_sockets
        end if
    next f

    sock.num=sock_num_bup

end sub

sub callback_system_error(byref tag as string, byref message as string)
    '****callback_system_error****'
    #IFDEF RETARGET_DEBUG_PRINT
        sys.trace("ERR-" + tag + ">" + message)
    #ELSE
        sys.debugprint("ERR-" + tag + ">" + message)
    #ENDIF
end sub
 `,
        });

        blocks.push({
            location: 'GLOBAL_DIMS',
            blockType: BlockType.CODE,
            template: 'dim interface_ready(MAX_NUM_INTERFACES) as no_yes',
        });

        blocks.push({
            location: 'GLOBAL_CONSTS',
            blockType: BlockType.CODE,
            template: `
const APP_VERSION="${APPLICATION_VERSION}"
const APP_ID="${APPLICATION_ID}"`,
        });

        // device info
        blocks.push({
            location: 'GLOBAL_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `
declare DEVICE_NAME as string(50)

declare TIOS_VERSION as string(30)
declare MAC_ADDRESS as string(20)`,
        });

        blocks.push({
            location: 'GLOBAL_DIMS',
            blockType: BlockType.CODE,
            template: `
dim TIOS_VERSION as string(30)
#IF PLATFORM_ID=WM2000 OR PLATFORM_ID=WS1102
dim MAC_ADDRESS as string(20)=wln.mac
#ELSE
dim MAC_ADDRESS as string(20)=net.mac
#ENDIF
dim DEVICE_NAME as string(50)="${DEVICE_NAME}"
`,
        });

        blocks.push({
            location: 'boot_complete',
            blockType: BlockType.CODE,
            template: `
TIOS_VERSION=mid(sys.version, 2,len(sys.version) - 2)
`,
        });

        blocks.push({
            location: 'on_sys_timer',
            blockType: BlockType.CODE,
            template: `
dim sys_timercountms as dword = sys.timercountms
#IF SYS_VER <> 1000 AND WATCHDOG_ENABLED
    sys.wdreset()
#ENDIF
`,
        });
        let bootTemplate = '';

        blocks.push({
            location: 'GLOBAL_DEFINES',
            blockType: BlockType.CODE,
            template: '#define WLN_AVAILABLE 0',
        });

        blocks.push({
            location: 'GLOBAL_DIMS',
            blockType: BlockType.CODE,
            template: 'dim current_interface as pl_sock_interfaces',
        });

        bootTemplate += 'current_interface = PL_SOCK_INTERFACE_NULL';

        blocks.push({
            location: 'GLOBAL_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `
declare APP_TIMEZONE as en_td_timezones
declare APP_LATITUDE as float
declare APP_LONGITUDE as float
${debugPrintOverride ? 'declare APP_LOGGING as string(10)' : ''}
`,
        });

        blocks.push({
            location: 'GLOBAL_DIMS',
            blockType: BlockType.CODE,
            template: `
dim APP_TIMEZONE as en_td_timezones
dim APP_LATITUDE as float
dim APP_LONGITUDE as float
${debugPrintOverride ? 'dim APP_LOGGING as string(10)' : ''}`,
        });

        blocks.push({
            location: 'GLOBAL_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `
declare current_interface as pl_sock_interfaces`,
        });

        // //////////////INTERFACE SWITCHING/////////////////
        blocks.push({
            location: 'GLOBAL_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `
declare sub change_current_interface(new_interface as pl_sock_interfaces)`,
        });

        blocks.push({
            location: 'DEVICE_TBS',
            blockType: BlockType.LOCATION,
            template: `
sub change_current_interface(new_interface as pl_sock_interfaces)
    if current_interface<>new_interface then
        '****services_stop****'
        
        sock.inconenabledmaster=NO
        close_interface_sockets(current_interface)

        current_interface=new_interface        
        
        select case current_interface
            case PL_SOCK_INTERFACE_NET: sys.debugprint("Set current interface to Ethernet\\r\\n") 
        
            case PL_SOCK_INTERFACE_WLN: sys.debugprint("Set current interface to Wi-Fi\\r\\n")
        
            case PL_SOCK_INTERFACE_PPP: sys.debugprint("Set current interface to Cellular\\r\\n")
        end select
        sock.inconenabledmaster=YES
        if new_interface <> PL_SOCK_INTERFACE_NULL then
            '****services_start****'
        end if
    end if
end sub`,
        });

        bootTemplate += `
    sys.onsystimerperiod=50
    #IF SYS_VER <> 1000 AND WATCHDOG_ENABLED
        sys.wdenabled=YES
        sys.wdautoreset=DISABLED
        sys.wdperiod=1000
    #ENDIF

    dim f as byte
    for f=0 to MAX_NUM_INTERFACES-1
        interface_ready(f)=NO
    next f`;

        // ////////////////////////////////////////////////

        blocks.push({
            location: 'boot',
            blockType: BlockType.CODE,
            template: bootTemplate,
        });

        blocks.push({
            location: 'boot_complete',
            blockType: BlockType.CODE,
            template: `
#IF SYS_VER <> 1000
if sys.extresettype=PL_SYS_EXT_RESET_TYPE_WATCHDOG then
    sys.debugprint("System reset by watchdog\\r\\n")
else if sys.extresettype=PL_SYS_EXT_RESET_TYPE_BROWNOUT then
    sys.debugprint("System reset by brownout\\r\\n")
end if
#ENDIF
`,
        });

        blocks.push({
            location: 'GLOBAL_DECLARATIONS',
            blockType: BlockType.CODE,
            template: 'declare sub close_interface_sockets(interface as pl_sock_interfaces)',
        });

        const BEEP = FeatureUtils.getCodeItem('beep', this.project);
        if (FeatureUtils.isSupported(BEEP, this.project)) {
            const BEEPFREQUENCY = this.getSettingValue('BEEPFREQUENCY');

            if (project.device.platform === 'WM2000' || project.device.platform === 'WS1102') {
                blocks.push({
                    location: 'boot',
                    blockType: BlockType.CODE,
                    template: `beep.frequency=${BEEPFREQUENCY}`,
                });
            } else {
                const CLOCK = 120000000;
                const freq = Math.floor(CLOCK / (4 * BEEPFREQUENCY));
                blocks.push({
                    location: 'boot',
                    blockType: BlockType.CODE,
                    template: `
    beep.divider = ${freq}`,
                });
            }
        }

        if (debugPrintOverride) {
            blocks.push({
                location: 'PROJECT_DEFINES',
                blockType: BlockType.CODE,
                id: 'RETARGET_DEBUG_PRINT',
                template: '/dRETARGET_DEBUG_PRINT=1',
            });

            blocks.push({
                location: 'boot',
                blockType: BlockType.CODE,
                template: `APP_LOGGING=${LOGGING}`,
            });

            let lcdLoggingCode = '';
            if (this.project.device.platform === 'TPP2W(G2)') {
                const TPB = project.getSettingValue('boardsetting', 'TPB');
                if (TPB === 'tpb2lg2') {
                    lcdLoggingCode = `
if APP_LOGGING="lcd_log" then
    lcd_log(s)
end if`;
                }
            }

            blocks.push({
                location: 'DASHBOARD_SETUP_STATE',
                blockType: BlockType.LOCATION,
                template: `
            "logEnabled": true,
            "firmwareUploadEnabled": true,`,
            });

            blocks.push({
                location: `var_${LOGGING}_updated`,
                blockType: BlockType.LOCATION,
                template: `
APP_LOGGING=${LOGGING}`,
            });

            blocks.push({
                location: 'GLOBAL_DECLARATIONS',
                blockType: BlockType.CODE,
                id: 'declare sub sys.debugprint(byref s as string)',
                template: 'declare sub sys.debugprint(byref s as string)',
            });

            blocks.push({
                location: 'DEVICE_TBS',
                blockType: BlockType.LOCATION,
                id: 'sub sys.debugprint(byref s as string)',
                template: `
sub sys.debugprint(byref s as string)
    sys.trace(s)
    ${lcdLoggingCode}
#IFDEF TBL_MAX_TOTAL_NUM_FIELDS
    if APP_LOGGING="tbl_log" then

    dim prev_fd_num as byte=fd.filenum
    dim debug_table as tbl_type
    dim prev_tbl_name as string(20)=tbl_get_table_name()
    dim tbl_select_status as en_tbl_status_codes
    if prev_tbl_name<>"DEBUG" or debug_table.filenum<>prev_fd_num then
        tbl_select_status = tbl_select("DEBUG","DEBUG")
        if tbl_select_status <> EN_TBL_STATUS_OK then
            exit sub
        end if
    end if
    tbl_select_status = tbl_get_table_info("DEBUG", debug_table)
    if tbl_select_status<>EN_TBL_STATUS_OK then
        exit sub
    end if
    if right(s,2)="\\r\\n" then
        s=left(s,len(s)-2)
    end if
    
    tbl_delete_old_records("DEBUG")
    tbl_field_sg("timestamp",lstr(datetime_current_timestamp()),EN_TBL_SET)
    tbl_field_sg("value",left(s,121),EN_TBL_SET)
    tbl_record_add("")
    if prev_tbl_name<>"DEBUG" AND prev_tbl_name<>"" then
        tbl_select(prev_tbl_name,prev_tbl_name)
    end if
    fd.filenum=prev_fd_num
    end if
#ENDIF
end sub`,
            });
        }

        blocks.push({
            location: 'boot_complete',
            blockType: BlockType.CODE,
            template: `
    pat.play("B-B-B-",PL_PAT_CANINT)`,
        });

        if (capabilities.includes('tios_g1')) {
            blocks.push({
                location: 'GLOBAL_DECLARATIONS',
                blockType: BlockType.CODE,
                template: `
declare function sys.timercountms() as dword
declare function sys.timercount32() as dword

declare function ExpN(Power as real) as real
declare function Exp10(Power as real) as real
declare function LogN(Power as real) as real
declare function Log10(Power as real) as real

declare function nPowr(Base as real,Power as real) as real
declare function nRoot(Base as real,Root as real) as real

declare function Deg2Rad(Angle as real) as real
declare function Rad2Deg(Angle as real) as real

declare function sin(Angle as real) as real
declare function cos(Angle as real) as real
declare function tan(Angle as real) as real

declare function atan(Ratio as real) as real
declare function asin(Ratio as real) as real
declare function acos(Ratio as real) as real
declare function atan2(r1 as real, r2 as real) as real

declare function ValF(byref Text as string) as real`,
            });

            blocks.push({
                location: 'DEVICE_TBS',
                blockType: BlockType.CODE,
                template: `
function sys.timercountms() as dword
    sys.timercountms=sys.timercount 
    sys.timercountms = sys.timercountms* 500
end function

function sys.timercount32() as dword
    sys.timercount32=sys.timercount
end function

const PI2 = 6.2831856
const ln10 = 2.3025851


'-----------------------------------------------
function ExpN(Power as real) as real
    dim F,P,I,L,M as real
    dim Count as byte
    F = Power
    P = 1 + Power
    I = 1
    Count = 0
    do
        I = I + 1
        F = F * (Power / I)
        M = L
        L = P
        P = P + F
        Count = Count + 1
    loop until (M = P) or (Count > 50)
    ExpN = P
end function
'-----------------------------------------------
function Exp10(Power as real) as real
    Exp10 = ExpN(Power * ln10)
end function
'-----------------------------------------------
function LogN(Power as real) as real
    dim P,E,N,A,B,L,R as real
    dim Count as byte
    E = ExpN(1)
    P = Power
    N = 0
    do while P >= E
        P = P / E
        N = N + 1
    loop
    N = N + (P / E)
    P = Power
    Count = 0
    do
        B = A
        A = N
        L = (P / (ExpN(N - 1)))
        R = ((N - 1) * E)
        N = ((L + R) / E)
        Count = Count + 1
    loop until (N = B) or (Count > 50)
    LogN = N
end function
'-----------------------------------------------
function Log10(Power as real) as real
    Log10 = (LogN(Power) / ln10)
end function
'-----------------------------------------------
function nPowr(Base as real,Power as real) as real
    if Power = 0 then
        nPowr = 1
    else if Base <= 0 then
        nPowr = 0
    else
        nPowr = ExpN(Logn(Base) * Power)
    end if
end function
'-----------------------------------------------
function nRoot(Base as real,Root as real) as real
    if Root = 0 then
        nRoot = 0
    else if Base <= 0 then
        nRoot = 0
    else
        nRoot = ExpN(Logn(Base) / Root)
    end if
end function
'-----------------------------------------------
'-----------------------------------------------
function Deg2Rad(Angle as real) as real
Deg2Rad = Angle / 360 * PI2
end function
'-----------------------------------------------
function Rad2Deg(Angle as real) as real
Rad2Deg = Angle * 360 / PI2
end function
'-----------------------------------------------
function sin(Angle as real) as real
    dim A,N,Y,S,Z,P,F as real
    dim Count as byte
    A = Angle
    do while A > PI2
        A = A - PI2
    loop
    do while A < 0
        A = A + PI2
    loop
    N = 2
    Y = A
    S = -1
    P = A
    F = 1
    Count = 0
    do
        Z = Y
        P = P * A * A
        F = F * N * (N+1)
        N = N + 2
        Y = Y + S * (P / F)
        S = S * -1
        Count = Count + 1
    loop until (Y = Z) or (Count > 30)
    Sin = Y
end function
'-----------------------------------------------
function Cos(Angle as real) as real
    dim A,N,Y,S,Z,P,F as real
    dim Count as byte
    A = Angle
    do while A > PI2
        A = A - PI2
    loop
    do while A < 0
        A = A + PI2
    loop
    N = 1
    Y = 1
    S = -1
    P = 1
    F = 1
    Count = 0
    do
        Z = Y
        P = P * A * A
        F = F * N * (N+1)
        N = N + 2
        Y = Y + S * (P / F)
        S = S * -1
        Count = Count + 1
    loop until (Y = Z) or (Count > 30)
    Cos = Y
end function
'-----------------------------------------------
function tan(Angle as real) as real
    Tan = Sin(Angle) / Cos(Angle)
end function
'-----------------------------------------------
function atan(Ratio as real) as real
    dim R,S,X,Y as real
    R = Abs(Ratio)
    IF Abs(Ratio) > 1 then R = 1 / R
    IF R > 0.267949192 then
        R = (1.732051 * R - 1) / (1.732051 + R)
        S = R * R * R
        Y = R - S / 3
        S = S * R * R
        Y = Y + S / 5
        S = S * R * R
        Y = Y - S / 7
        S = S * R * R
        Y = Y + S / 9
        S = S * R * R
        Y = Y - S / 11
        S = S * R * R
        Y = Y + S / 13
        Y = 0.52359877 + Y
    else
        S = R * R * R
        Y = R - S / 3
        S = S * R * R
        Y = Y + S / 5
        S = S * R * R
        Y = Y - S / 7
        S = S * R * R
        Y = Y + S / 9
        S = S * R * R
        Y = Y - S / 11
        S = S * R * R
        Y = Y + S / 13
    end if
    if Abs(Ratio) > 1 then Y = 1.5707963 - Y
    atan = Y * Sgn(Ratio)
end function
'-----------------------------------------------
function asin(Ratio as real) as real
    asin = atan(Ratio / nRoot(1 - Ratio * Ratio,2))
end function
'-----------------------------------------------
function acos(Ratio as real) as real
    acos = atan(nRoot(1 - Ratio * Ratio,2) / Ratio)
end function
'-----------------------------------------------
function atan2(r1 as real, r2 as real) as real
end function
'-----------------------------------------------
function ValF(byref Text as string) as real
    dim X,C as byte
    dim R,N,P as long
    dim F as boolean
    R = 0 :N = 1 :P = 1 :F = false
    for X = 1 to len(Text)
        C = asc(mid(Text,X,1))
        if (C <> 32) or R > 0 then 'Skip leading spaces
            if (C = 45) and (R = 0) and (F = false) then 'Negative sign if in front
                N = -1
            else
                if ((C = 44) or (C = 46)) and (F = false) then 'Decimal point or comma
                    F = true
                else
                    if (C >= 48) and (C <= 57) then
                        if R < 214748364 then
                            R = R * 10 + (C - 48)
                            if F then N = N * 10
                        else
                            if F = 0 then P = P * 10
                        end if
                    else
                        exit for
                    end if
                end if
            end if
        end if
    next X
    ValF = 1.0 * R / N * P
end function

`,
            });
        }

        if ((WIFI_ENABLED === '1' || CELLULAR_ENABLED === '1')
            && (this.project.device.deviceType === 'TPP'
                || this.project.device.id === 'DS1102N'
                || this.project.device.id === 'DS1101N')) {
            blocks.push({
                location: 'GLOBAL_DECLARATIONS',
                blockType: BlockType.CODE,
                template: `
#if SIGNAL_STRENGTH_INDICATION
declare sub signal_strength_set(strength as byte)
declare sub signal_strength_indication_init()
#endif`,
            });

            blocks.push({
                location: 'boot',
                blockType: BlockType.CODE,
                template: `
#if SIGNAL_STRENGTH_INDICATION
    signal_strength_indication_init()
    signal_strength_set(0) 'this will turn all 5 LEDs OFF
#endif`,
            });

            blocks.push({
                location: 'DEVICE_TBS',
                blockType: BlockType.LOCATION,
                id: 'signal_strength_set',
                template: `

#if SIGNAL_STRENGTH_INDICATION=1 and PLATFORM_ID <> WS1101 and PLATFORM_ID <> WS1102
sub signal_strength_indication_init()
    dim f as byte
    
    'make sure there is no reset
    io.num=PL_IO_NUM_46
    io.enabled=YES
    io.state=HIGH

    'we will be turning LEDs off
    io.num=PL_IO_NUM_48
    io.enabled=YES
    io.state=HIGH
    'make 5 clocks
    io.num=PL_IO_NUM_47
    io.enabled=YES
    for f=0 to 4
        io.state=LOW
        io.state=HIGH
    next f
end sub
#endif    

#if SIGNAL_STRENGTH_INDICATION=2
sub signal_strength_indication_init()
    
    io.num=PL_IO_NUM_19_SB1
    io.enabled=YES
    io.state=HIGH

    io.num=PL_IO_NUM_20_SB2
    io.enabled=YES
    io.state=HIGH

    io.num=PL_IO_NUM_21_SB3
    io.enabled=YES
    io.state=HIGH

    io.num=PL_IO_NUM_22_SB4
    io.enabled=YES
    io.state=HIGH

    io.num=PL_IO_NUM_23_SB5
    io.enabled=YES
    io.state=HIGH
end sub
#endif    

'--------------------------------------------------------------------
#if SIGNAL_STRENGTH_INDICATION=1 and PLATFORM_ID <> WS1101 and PLATFORM_ID <> WS1102
sub signal_strength_set(strength as byte)
'Bits 0-5 of strength argument correspond to LEDs    
    
    dim f,x as byte
    dim s as string(8)
    
    s=mid(bin(strength),3,8)
    s=strgen(8-len(s),"0")+s    
    
    'make 5 clocks
    io.num=PL_IO_NUM_47
    for f=0 to 4
        x=1-val(mid(s,f+4,1))
        io.lineset(PL_IO_NUM_48,x)
        io.state=LOW
        io.state=HIGH
    next f
end sub
#endif    

#if SIGNAL_STRENGTH_INDICATION=2
sub signal_strength_set(strength as byte)
'Bits 0-5 of strength argument correspond to LEDs    
    
    if strength and &b00001 then
        io.lineset(PL_IO_NUM_19_SB1,LOW)
    else
        io.lineset(PL_IO_NUM_19_SB1,HIGH)
    end if

    if strength and &b00010 then
        io.lineset(PL_IO_NUM_20_SB2,LOW)
    else
        io.lineset(PL_IO_NUM_20_SB2,HIGH)
    end if

    if strength and &b00100 then
        io.lineset(PL_IO_NUM_21_SB3,LOW)
    else
        io.lineset(PL_IO_NUM_21_SB3,HIGH)
    end if

    if strength and &b01000 then
        io.lineset(PL_IO_NUM_22_SB4,LOW)
    else
        io.lineset(PL_IO_NUM_22_SB4,HIGH)
    end if

    if strength and &b10000 then
        io.lineset(PL_IO_NUM_23_SB5,LOW)
    else
        io.lineset(PL_IO_NUM_23_SB5,HIGH)
    end if
end sub
#endif    

`,
            });
        }

        return blocks;
    }

    getDetails(): string {
        return 'This is the section for generic system level settings.';
    }
}
